// src/components/Pentests/tabs/Reports.js
//
// Generate / download / delete docx reports for a pen test.
// Two kinds: Internal (long, full detail + OWASP appendix) and Client
// (sanitized executive summary + findings table).

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Briefcase, Download, FileText, Lock, Trash2 } from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { EmptyState, PrimaryCTA } from '../../common/Shinobi';
import ConfirmModal from '../../common/ConfirmModal';
import { formatAbsoluteDate } from '../../../utils/time';

const KIND_LABEL = { internal: 'Internal', client: 'Client' };
const KIND_ICON = { internal: Lock, client: Briefcase };
const KIND_BADGE = {
  internal: 'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
  client: 'bg-cyan-100 text-cyan-700 border-cyan-300',
};

function fmt(ts) {
  return ts ? formatAbsoluteDate(ts) : '-';
}

export default function Reports() {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const { isPentester } = useAuth();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(null); // 'internal' | 'client' | null
  const [downloading, setDownloading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, report: null });
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const data = await pentestsAPI.reports.list(pentestId);
      setReports(data || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const onGenerate = async (kind) => {
    setGenerating(kind);
    setError(null);
    try {
      await pentestsAPI.reports.generate(pentestId, kind);
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setGenerating(null);
    }
  };

  const onDownload = async (report) => {
    setDownloading(report.id);
    try {
      const resp = await pentestsAPI.reports.download(pentestId, report.id);
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.filename || `report_${report.id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDownloading(null);
    }
  };

  const onDeleteConfirm = async () => {
    if (!confirmDelete.report) return;
    setDeleting(true);
    try {
      await pentestsAPI.reports.remove(pentestId, confirmDelete.report.id);
      setConfirmDelete({ isOpen: false, report: null });
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading reports...</div>;

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Generate panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GenerateCard
          kind="internal"
          icon={Lock}
          title="Internal Report"
          description="Long-form report with full per-finding detail, scope, AI remediation analyses (when present), and an OWASP Top 10 appendix. For the Wasabi Red Team and stakeholders."
          generating={generating === 'internal'}
          disabled={!isPentester || generating !== null}
          onGenerate={() => onGenerate('internal')}
        />
        <GenerateCard
          kind="client"
          icon={Briefcase}
          title="Client Report"
          description="Sanitized executive summary plus a high-level findings table. Internal IPs and detailed scope descriptions are omitted; suitable for sharing with non-Wasabi audiences."
          generating={generating === 'client'}
          disabled={!isPentester || generating !== null}
          onGenerate={() => onGenerate('client')}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-700">
            Generated Reports
          </h3>
          <span className="text-xs text-slate-500">
            {reports.length} {reports.length === 1 ? 'report' : 'reports'}
          </span>
        </div>

        {reports.length === 0 ? (
          <EmptyState
            icon={FileText}
            accent="green"
            title="No reports generated yet"
            description="Click Generate above to produce an Internal or Client report. Each generation captures a frozen snapshot of the engagement."
          />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
            <table className="w-full text-sm text-left text-slate-800">
              <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
                <tr>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3 text-right">Findings</th>
                  <th className="px-4 py-3">Generated</th>
                  <th className="px-4 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const Icon = KIND_ICON[r.kind] || FileText;
                  const badgeClass = KIND_BADGE[r.kind] || KIND_BADGE.internal;
                  return (
                    <tr key={r.id} className="border-t border-cyber-300/30 hover:bg-cyber-200/40">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${badgeClass}`}>
                          <Icon className="w-3 h-3" />
                          {KIND_LABEL[r.kind] || r.kind}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{r.title}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.finding_count}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(r.generated_at || r.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => onDownload(r)}
                            disabled={downloading === r.id}
                            title="Download .docx"
                            className="p-1.5 rounded text-slate-500 hover:text-shinobi-green hover:bg-wasabi-100 disabled:opacity-50"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isPentester && (
                            <button
                              type="button"
                              onClick={() => setConfirmDelete({ isOpen: true, report: r })}
                              title="Delete report"
                              className="p-1.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-100"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => !deleting && setConfirmDelete({ isOpen: false, report: null })}
        onConfirm={onDeleteConfirm}
        title="Delete Report"
        message={
          confirmDelete.report
            ? `Permanently delete "${confirmDelete.report.title}"? This cannot be undone.`
            : ''
        }
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

function GenerateCard({ kind, icon: Icon, title, description, generating, disabled, onGenerate }) {
  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          kind === 'internal'
            ? 'border border-wasabi-300 bg-wasabi-100 text-wasabi-700'
            : 'border border-cyan-300 bg-cyan-100 text-cyan-700'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            {kind === 'internal' ? 'Long version' : 'Sanitized'}
          </div>
          <h4 className="text-base font-semibold text-slate-900">{title}</h4>
        </div>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed flex-1">{description}</p>
      <div className="flex justify-end">
        <PrimaryCTA
          variant={kind === 'internal' ? 'green' : 'green'}
          icon={FileText}
          iconPosition="left"
          loading={generating}
          disabled={disabled}
          onClick={onGenerate}
        >
          {generating ? 'Generating...' : 'Generate'}
        </PrimaryCTA>
      </div>
    </div>
  );
}
